// Session summaries + CSV export (+ optional flat-labor compare).

import { SectionCard } from "@/components/common/SectionCard";
import { mfgSecondaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { TimeStudyEntry } from "@/lib/manufacturing/timeStudyClient";
import {
  downloadTextFile,
  formatDurationSeconds,
  summarizeTimeStudyEntries,
  timeStudyEntriesToCsv,
} from "@/lib/manufacturing/timeStudyFormat";

type BuildModelRef = {
  id: string;
  name: string;
  flat_labor_rate_usd: number;
};

type Props = {
  sessionTitle: string;
  entries: TimeStudyEntry[];
  buildModels: BuildModelRef[];
};

function BucketTable({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ label: string; count: number; durationSeconds: number }>;
}) {
  if (!rows.length) return null;
  return (
    <div>
      <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
        {title}
      </h4>
      <ul className="space-y-1 text-sm text-gray-800 dark:text-zinc-200">
        {rows.map((r) => (
          <li key={r.label} className="flex justify-between gap-3">
            <span className="truncate">
              {r.label}{" "}
              <span className="text-xs text-gray-500">({r.count})</span>
            </span>
            <span className="shrink-0 tabular-nums">{formatDurationSeconds(r.durationSeconds)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TimeStudySummaries({ sessionTitle, entries, buildModels }: Props) {
  const summary = summarizeTimeStudyEntries(entries);
  const modelById = new Map(buildModels.map((m) => [m.id, m]));

  const laborCompares = entries
    .filter((e) => e.build_model_id && modelById.has(e.build_model_id))
    .map((e) => {
      const model = modelById.get(e.build_model_id!)!;
      const hours = e.duration_seconds / 3600;
      const implied = hours * model.flat_labor_rate_usd;
      return {
        id: e.id,
        subject: e.subject_label,
        modelName: model.name,
        flatRate: model.flat_labor_rate_usd,
        hours,
        impliedLaborUsd: implied,
      };
    });

  const exportCsv = () => {
    const safe = sessionTitle.replace(/[^\w\-]+/g, "_").slice(0, 40) || "session";
    downloadTextFile(
      `time-study-${safe}-${new Date().toISOString().slice(0, 10)}.csv`,
      timeStudyEntriesToCsv(entries),
    );
  };

  return (
    <SectionCard
      title="Summaries"
      rightSlot={
        <button
          type="button"
          className={mfgSecondaryBtnClass}
          disabled={entries.length === 0}
          onClick={exportCsv}
        >
          Export CSV
        </button>
      }
    >
      {entries.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">No observations yet.</p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-zinc-300">
            Session total:{" "}
            <span className="font-semibold tabular-nums">
              {formatDurationSeconds(summary.totalSeconds)}
            </span>{" "}
            across {entries.length} observation{entries.length === 1 ? "" : "s"}.
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            <BucketTable title="By subject" rows={summary.bySubject} />
            <BucketTable title="By personnel" rows={summary.byPersonnel} />
            <BucketTable title="By day" rows={summary.byDay} />
          </div>
          {laborCompares.length > 0 ? (
            <div>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                Flat labor compare (read-only)
              </h4>
              <ul className="space-y-1 text-sm text-gray-800 dark:text-zinc-200">
                {laborCompares.map((c) => (
                  <li key={c.id} className="flex flex-wrap justify-between gap-2">
                    <span className="truncate">
                      {c.subject}{" "}
                      <span className="text-xs text-gray-500">
                        vs {c.modelName} @ ${c.flatRate.toFixed(2)}/hr
                      </span>
                    </span>
                    <span className="shrink-0 tabular-nums">
                      ~${c.impliedLaborUsd.toFixed(2)} ({c.hours.toFixed(2)} h)
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </SectionCard>
  );
}
